import { React, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './Navbar.scss';
import Avatar from '@mui/material/Avatar';
import { useProfile } from '../../contexts/ProfileContext';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import { makeStyles } from 'tss-react/mui';
import { useAuthentication } from '../../contexts/Authentication';
import Navigation from './Navigation';
import Persona from '../persona/Persona';

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'relative',
  },
  avatarDropdown: {
    position: 'absolute',
    top: -35,
    right: 0,
    left: 50,
    zIndex: 20,
    width: '150px',
    border: '1px solid',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
}));

function Navbar({ routes, personaUser, personaStatus, setPersonaStatus, ...props }) {
  const { profilePictureUrl, loading: profilePictureLoading, userInfo } = useProfile();
  const [avatarLoading, setAvatarLoading] = useState(profilePictureLoading);
  const [avatarError, setAvatarError] = useState(false);
  const avatarSrc = avatarError ? undefined : profilePictureUrl;
  // Get user's initial for fallback
  const userInitial = userInfo?.firstName ? userInfo.firstName[0].toUpperCase() : '';

  // Reset avatarLoading when profilePictureUrl changes
  useEffect(() => {
    // Set loading to false as soon as we have a valid profilePictureUrl and not loading from context
    if (profilePictureUrl && !profilePictureLoading) {
      setAvatarLoading(false);
    } else {
      setAvatarLoading(profilePictureLoading);
    }
  }, [profilePictureLoading, profilePictureUrl]);
  const { logout } = useAuthentication();

  const { classes } = useStyles();
  const [avatarOpen, setAvatarOpen] = useState(false);

  const handleAvatarClick = () => {
    setAvatarOpen((prev) => !prev);
  };

  const handleAvatarClickAway = () => {
    setAvatarOpen(false);
  };

  return (
    <div className="navbar-wrapper">
      <Container className="navbar-container">
        <div className="navigation">
          <Navigation navItems={Object.values(routes)} {...props} />
        </div>

        <ClickAwayListener onClickAway={handleAvatarClickAway}>
          <div className="root">
            <div className="persona_container">
              {personaUser && (
                <Persona
                  personaUser={personaUser}
                  personaStatus={personaStatus}
                  setPersonaStatus={setPersonaStatus}
                />
              )}
            </div>
            <Avatar
              data-cy="navbar-Avatar-button"
              src={avatarLoading ? undefined : avatarSrc}
              onClick={handleAvatarClick}
              sx={{
                bgcolor: avatarError || !profilePictureUrl ? '#1976d2' : '#fff',
                width: 40,
                height: 40,
                fontSize: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              slotProps={{
                img: {
                  loading: "lazy",
                  style: { width: '100%', height: '100%', objectFit: 'cover' },
                  onLoad: (e) => {
                    setTimeout(() => setAvatarLoading(false), 3000);
                  },
                  onError: (e) => {
                    console.error('Avatar image failed to load:', e.target.src);
                    setAvatarError(true);
                    setAvatarLoading(false);
                  }
                }
              }}
            >
              {
                (() => {
                  if (avatarLoading) {
                    return (
                      <svg width="24" height="24" viewBox="0 0 24 24" style={{ margin: 'auto', display: 'block' }}>
                        <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="4" fill="none" strokeDasharray="60" strokeDashoffset="40">
                          <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                        </circle>
                      </svg>
                    );
                  }
                  if (!avatarSrc || avatarError) {
                    return userInitial;
                  }
                  return null;
                })()
              }
            </Avatar>
            {avatarOpen ? (
              <div className={classes.avatarDropdown}>
                <Link data-cy="navbar-ReturnHub-button" to="/welcome">
                  Return to Hub
                </Link>
                <Link data-cy="navbar-MyProfile-button" to="/profile">
                  My Profile
                </Link>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link data-cy="navbar-Logout-button" onClick={logout}>
                  Logout
                </Link>
              </div>
            ) : null}
          </div>
        </ClickAwayListener>
      </Container>
    </div>
  );
}

Navbar.propTypes = {
  routes: PropTypes.object.isRequired,
  personaUser: PropTypes.object,
  personaStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  setPersonaStatus: PropTypes.func.isRequired,
};

export default Navbar;
